import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../api/client';
import { ArrowLeft, Mail, Phone, Calendar, Tag, ExternalLink, Briefcase, User } from 'lucide-react';

interface Candidate {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  stageTitle?: string;
  vacancyTitle?: string;
  vacancyId?: string;
  createdAt: string;
}

const CandidateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        const [candRes, stagesRes] = await Promise.all([api.get(`/candidates/${id}`), api.get('/candidate-stages')]);
        const c = candRes.data;
        const stages = stagesRes.data;
        const stageMap = new Map(stages.map((s: any) => [s._id, s.title]));

        let vacancyTitle = '—';
        if (c.vacancyId) {
          try { const vacRes = await api.get(`/vacancies/${c.vacancyId}`); vacancyTitle = vacRes.data.title || '—'; } catch (e) {}
        }

        setCandidate({
          ...c,
          name: c.firstName && c.lastName ? `${c.firstName} ${c.lastName}`.trim() : c.name || 'Без имени',
          email: c.email || '', phone: c.phone || '',
          stageTitle: stageMap.get(c.stage) || '—',
          vacancyTitle, vacancyId: c.vacancyId,
        });
      } catch (err) {
        setError('Не удалось загрузить данные кандидата.');
      } finally {
        setLoading(false);
      }
    };
    fetchCandidate();
  }, [id]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Загрузка...</div>;
  if (error) return <div style={{ padding: 60, textAlign: 'center', color: '#ff4d4f' }}>{error}</div>;
  if (!candidate) return null;

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate('/app/recruitment-analytics/candidates')} style={styles.backBtn}>
          <ArrowLeft size={16} /> Назад к кандидатам
        </button>
        <button onClick={() => navigate(`/app/hr/candidate/${candidate._id}`)} style={{ ...styles.backBtn, borderColor: '#10b981', color: '#10b981' }}>
          <ExternalLink size={16} /> Открыть в CRM
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 24 }}>
        {/* Профиль */}
        <div style={{ ...styles.glassCard, textAlign: 'center', padding: '32px 24px' }}>
          <div style={{ width: 100, height: 100, borderRadius: '50%', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, fontWeight: 800, margin: '0 auto 20px' }}>
            {candidate.name.charAt(0)}
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#1a1a2e', margin: '0 0 8px 0' }}>{candidate.name}</h2>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(37,117,252,0.1)', color: '#2575fc', padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
            <Tag size={14} /> {candidate.stageTitle}
          </div>
        </div>

        {/* Детали */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={styles.glassCard}>
            <h3 style={styles.sectionTitle}>Контактная информация</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={styles.infoRow}><Mail size={18} color="#64748b" /> {candidate.email || '—'}</div>
              <div style={styles.infoRow}><Phone size={18} color="#64748b" /> {candidate.phone || '—'}</div>
              <div style={styles.infoRow}><Calendar size={18} color="#64748b" /> Добавлен: {new Date(candidate.createdAt).toLocaleDateString('ru-RU')}</div>
            </div>
          </div>

          <div style={styles.glassCard}>
            <h3 style={styles.sectionTitle}>Связанная вакансия</h3>
            {candidate.vacancyId ? (
              <div onClick={() => navigate(`/app/recruitment-analytics/vacancies/${candidate.vacancyId}`)} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'rgba(255,255,255,0.4)', borderRadius: 16, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.05)' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,117,252,0.1)', color: '#2575fc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Briefcase size={20} /></div>
                <div style={{ flex: 1, fontWeight: 600, color: '#1a1a2e' }}>{candidate.vacancyTitle}</div>
                <ExternalLink size={16} color="#94a3b8" />
              </div>
            ) : <p style={{ color: '#94a3b8' }}>Не привязан к вакансии</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, padding: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px 0' },
  infoRow: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, color: '#4a4a68' },
  backBtn: { background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(10px)', border: '1px solid #2575fc', color: '#2575fc', borderRadius: 30, padding: '8px 16px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 14 },
};

export default CandidateDetail;
